import React from 'react';
import { Scissors } from 'lucide-react';
import './themes.css';
import InstacutDefaultShop from './InstacutDefaultShop';

// ============================================================
// Registry of the 12 themes. New shops default to 'clean-editorial'.
// Each entry: { slug, label, signature }. The rendering layer lives
// in <ShopThemed> below; per-theme markup tweaks are gated by slug
// (preamble bands, hero crests, dotted-leader service rows, etc.).
// ============================================================
// LOCK STATE — every non-default theme is temporarily locked. To
// re-enable a theme after it's been fixed and tested, flip its
// `locked` value to `false` (or delete the property) — that's the
// only change required in this file. `instacut-default` intentionally
// carries no `locked` flag so it stays selectable in every UI path.
export const THEMES = [
  { slug: 'instacut-default',     label: 'Instacut default', isDefault: true },
  { slug: 'heavyweight-swiss',    label: 'Heavyweight Swiss',                     locked: true },
  { slug: 'midnight-chrome',      label: 'Midnight Chrome',                       locked: true },
  { slug: 'acid-sport',           label: 'Acid Sport',                            locked: true },
  { slug: 'warm-heritage',        label: 'Warm Heritage',                         locked: true },
  { slug: 'clean-editorial',      label: 'Clean Editorial',                       locked: true },
  { slug: 'soft-pastel',          label: 'Soft Pastel',                           locked: true },
  { slug: 'brutalist-mono',       label: 'Brutalist Mono',                        locked: true },
  { slug: 'jewel-luxe',           label: 'Jewel Luxe',                            locked: true },
  { slug: 'kinetic-streetwear',   label: 'Kinetic Streetwear', animated: true,    locked: true },
  { slug: 'living-aurora',        label: 'Living Aurora',      animated: true,    locked: true },
  { slug: 'cinematic-split',      label: 'Cinematic Split',                       locked: true },
  { slug: 'gentlemans-club',      label: "Gentleman's Club",                      locked: true },
];

export const DEFAULT_THEME = 'instacut-default';
const THEME_SLUGS = new Set(THEMES.map(t => t.slug));
export const normaliseTheme = (slug) => (slug && THEME_SLUGS.has(slug) ? slug : DEFAULT_THEME);

// Each locked theme carries its own accent. `instacut-default` is null so
// the shop admin's hand-picked accent_colour keeps effect. Kept in-sync
// with the per-theme CSS in `themes.css`.
export const THEME_ACCENTS = {
  'instacut-default':    null,
  'heavyweight-swiss':   '#111111',
  'midnight-chrome':     '#c0c0d1',
  'acid-sport':          '#c4ff00',
  'warm-heritage':       '#8b5a2b',
  'clean-editorial':     '#111827',
  'soft-pastel':         '#f5a3c2',
  'brutalist-mono':      '#000000',
  'jewel-luxe':          '#d4af37',
  'kinetic-streetwear':  '#ff5722',
  'living-aurora':       '#7c3aed',
  'cinematic-split':     '#eab308',
  'gentlemans-club':     '#a26b1f',
};

export const resolveAccent = (theme, fallback) =>
  THEME_ACCENTS[normaliseTheme(theme)] || fallback || '#4f46ff';

// ============================================================
// Helpers
// ============================================================
const initials = (s) =>
  (s || '').split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hh = parseInt(h, 10);
  return Number.isNaN(hh) ? t : `${hh}:${m || '00'}`;
};

// Some themes break the hero headline across two lines with a coloured/italic
// second half. The CSS targets that with `<span>` or `<em>` selectors. We
// don't have a separate "highlight half" field; we just split the shop name
// roughly in half on a space to mirror the mockups (e.g. "Sharp & Sons" →
// "Sharp" / "& Sons"). If there's no space, we keep it on one line.
const splitName = (name) => {
  const s = (name || '').trim();
  if (!s.includes(' ')) return [s, ''];
  const words = s.split(' ');
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
};

// ============================================================
// Single shared 9-section renderer.
// ============================================================
export const ShopThemed = ({
  theme,
  shop,
  services,
  barbers,
  hours,        // [{ label, closed, start, end }]
  showHours = true, // M4 — respect the shops.show_hours toggle from /profile
  onBook,       // (serviceId?) => void
  // Opt-in: only the live shop route (/shop/:subdomain) sets this true.
  // Every preview surface (profile Live preview, theme-picker thumbnails)
  // leaves it false so the fixed top bar can never portal to <body> and
  // leak out of the phone frame. Ancestor sniffing was too fragile —
  // every new transformed wrapper re-introduced the bug.
  allowPortal = false,
}) => {
  const slug = normaliseTheme(theme);
  const tagline = shop.tagline?.trim();
  const about   = shop.welcome_text?.trim();
  const reviews = Array.isArray(shop.reviews) ? shop.reviews.filter(r => r?.quote?.trim()) : [];
  const gallery = Array.isArray(shop.gallery_urls) ? shop.gallery_urls.filter(Boolean) : [];
  // M5 — prefer the structured address fields when set (better map
  // accuracy and consistent line-by-line render); fall back to the
  // legacy free-text `address` blob for shops that haven't migrated.
  const structuredParts = [
    shop.address_line_1,
    shop.address_line_2,
    shop.city,
    shop.postcode,
  ].map(v => (v || '').trim()).filter(Boolean);
  const structured = structuredParts.length > 0;
  const address = structured ? structuredParts.join(', ') : shop.address?.trim();
  // Map query uses the tightest signal available — postcode + city if
  // structured, else the free-text block. Google Maps behaves best
  // when the postcode is present.
  const mapQuery = structured
    ? [shop.city, shop.postcode].filter(v => v && v.trim()).join(', ')
    : address;
  const social  = shop.social_links || {};
  const socialEntries = Object.entries(social).filter(([, v]) => v && String(v).trim());
  const hasHours = (hours || []).some(h => !h.closed);
  // Landing page barber list — see the matching block in
  // InstacutDefaultShop.js for context. Supports both `photo_url`
  // (public URL) and `avatar_url` (Supabase storage key served via
  // the backend), and falls back to an initials tile so the section
  // never silently hides when barbers exist but photos aren't set.
  const BACKEND_URL_ENV = process.env.REACT_APP_BACKEND_URL || '';
  const barberPhotoSrc = (b) => {
    if (b.photo_url) return b.photo_url;
    if (b.avatar_url && BACKEND_URL_ENV) return `${BACKEND_URL_ENV}/api/barbers/${b.id}/avatar`;
    return null;
  };
  const presentableBarbers = (barbers || []).filter(b => (b.name || '').trim());
  const [namePart1, namePart2] = splitName(shop.name);
  // Map source — prefer the Maps Embed API with the shop's saved
  // `google_place_id` because it lands the pin exactly on the shop's
  // door (Google's canonical marker for the business), not the
  // postcode centroid the free `q=address&output=embed` embed uses.
  // Falls back to the free embed when either the key or the place_id
  // is missing so the map never disappears just because the shop
  // hasn't run the Places picker yet.
  const gmapsKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const rawPlaceId = (shop.google_place_id || '').trim();
  // Guard against malformed values (test iteration_61 found some shops
  // had OAuth callback strings stored here). Real Place IDs are
  // >=20 chars of [A-Za-z0-9_-], no whitespace.
  const placeId = /^[A-Za-z0-9_-]{20,}$/.test(rawPlaceId) ? rawPlaceId : '';
  const mapSrc = (() => {
    if (gmapsKey && placeId) {
      return `https://www.google.com/maps/embed/v1/place?key=${gmapsKey}&q=place_id:${encodeURIComponent(placeId)}`;
    }
    return mapQuery ? `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed` : null;
  })();
  const locality = structured
    ? (shop.city?.trim() || shop.postcode?.trim() || '')
    : (address ? (address.split(',').slice(-2, -1)[0]?.trim() || address.split('\n')[0]) : '');

  // Per-theme hero headline markup (the second half of the name is
  // wrapped in <span> / <em> so each theme's CSS can colour-pop it).
  const heroH1 = (() => {
    if (!namePart2) return <h1 data-testid="hero-name">{namePart1}</h1>;
    if (slug === 'midnight-chrome' || slug === 'warm-heritage' || slug === 'jewel-luxe' || slug === 'gentlemans-club') {
      return <h1 data-testid="hero-name">{namePart1} <i>{namePart2}</i></h1>;
    }
    if (slug === 'heavyweight-swiss' || slug === 'soft-pastel' || slug === 'cinematic-split') {
      return <h1 data-testid="hero-name">{namePart1}<br/><span>{namePart2}</span></h1>;
    }
    if (slug === 'acid-sport') {
      // Bebas Neue uppercase, outline second half
      return <h1 data-testid="hero-name">{namePart1.toUpperCase()}<br/><span className="o">{namePart2.toUpperCase()}</span></h1>;
    }
    if (slug === 'kinetic-streetwear') {
      return <h1 data-testid="hero-name">{namePart1}<br/><em>{namePart2}</em></h1>;
    }
    if (slug === 'living-aurora') {
      return <h1 data-testid="hero-name">{namePart1} <span>{namePart2}</span></h1>;
    }
    // clean-editorial, brutalist-mono — plain
    return <h1 data-testid="hero-name">{shop.name}</h1>;
  })();

  // Theme 11 puts the hero inside a dark clip-path band that sits above .shell.
  // Theme 04 puts the hero inside a curved green topband.
  // Theme 12 wraps everything in a deco brass frame.
  // Theme 09 prepends a scrolling marquee.
  // Theme 10 adds an absolutely-positioned aurora gradient bg.
  const wrapsHero = slug === 'cinematic-split' || slug === 'warm-heritage' || slug === 'gentlemans-club';

  // The Hero block content (rendered into either .top-band, .topband, .deco-in or normal .hero)
  const heroLogo = (slug === 'clean-editorial' || slug === 'soft-pastel') ? (
    <div
      className="logo"
      style={shop.logo_url ? { backgroundImage: `url(${shop.logo_url})`, color: 'transparent', borderColor: 'transparent' } : {}}
      aria-hidden="true"
    >
      {!shop.logo_url && initials(shop.name).charAt(0)}
    </div>
  ) : null;

  const heroBody = (
    <>
      {heroLogo}
      {slug === 'heavyweight-swiss' && tagline && (
        <div className="est">{tagline}</div>
      )}
      {(slug === 'midnight-chrome' || slug === 'jewel-luxe' || slug === 'brutalist-mono') && tagline && (
        <div className="est">{tagline}</div>
      )}
      {heroH1}
      {tagline && !['heavyweight-swiss','midnight-chrome','jewel-luxe','brutalist-mono','gentlemans-club'].includes(slug) && (
        <p data-testid="hero-tagline">{tagline}</p>
      )}
      {/* hero-internal CTA only for the clean-editorial theme (its mockup carries one) */}
      {slug === 'clean-editorial' && (
        <div className="cta-row">
          <button onClick={() => onBook()} data-testid="hero-book-btn">Book now</button>
        </div>
      )}
    </>
  );

  // Meta line content — same across themes; per-theme CSS styles the chips/grid
  const isFixedDeposit = (shop.deposit_type || 'fixed') === 'fixed';
  const showDepositBadge = shop.deposit_required && isFixedDeposit && Number(shop.deposit_amount) > 0;
  const showMeta = (barbers.length > 0) || showDepositBadge;
  const metaLine = showMeta ? (
    <div className="meta-line" data-testid="meta-line">
      {barbers.length > 0 && (
        <div><div className="k">Barbers</div><div className="v">{barbers.length}</div></div>
      )}
      {showDepositBadge && (
        <div><div className="k">Deposit</div><div className="v">£{Number(shop.deposit_amount).toFixed(0)}</div></div>
      )}
    </div>
  ) : null;

  // ----- Build the body of sections -----
  const Sections = (
    <>
      {about && (
        <section className="sec about" data-testid="section-about">
          <div className="sec-h">About</div>
          <p>{about}</p>
        </section>
      )}

      <section className="sec services" data-testid="section-services">
        <div className="sec-h">Services</div>
        {services.length === 0 ? (
          <p style={{ opacity: 0.6, fontSize: 14 }}>No services available yet.</p>
        ) : services.map((s, idx) => (
          <button
            key={s.id}
            className="svc"
            type="button"
            onClick={() => onBook(s.id)}
            data-testid={`service-${s.id}`}
          >
            <span className="num">{String(idx + 1).padStart(2, '0')}</span>
            <span className="icon" aria-hidden="true"><Scissors size={18} strokeWidth={1.75} /></span>
            <span className="l">
              <b>{s.name}</b>
              <small>{s.duration_min} min{s.description ? ` · ${s.description}` : ''}</small>
            </span>
            {/* dotted leader for theme 12 (CSS hides it elsewhere) */}
            {slug === 'gentlemans-club' && <span className="dots" aria-hidden="true" />}
            <span className="p">£{Number(s.price).toFixed(0)}</span>
          </button>
        ))}
      </section>

      {presentableBarbers.length > 0 && (
        <section className="sec barbers" data-testid="section-barbers">
          <div className="sec-h">Barbers</div>
          <div className="list">
            {presentableBarbers.map(b => {
              const src = barberPhotoSrc(b);
              return (
                <div className="b" key={b.id} data-testid={`barber-${b.id}`}>
                  <div
                    className="ph"
                    style={src ? { backgroundImage: `url(${src})` } : {}}
                    aria-hidden="true"
                  >
                    {!src && initials(b.name).charAt(0)}
                  </div>
                  <div className="nm">
                    <b>{b.name}</b>
                    {b.role && <small>{b.role}</small>}
                    {b.bio && <p>{b.bio}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {gallery.length > 0 && (
        <section className="sec gallery" data-testid="section-gallery">
          <div className="sec-h">Gallery</div>
          <div className="grid">
            {gallery.map((url) => (
              <div key={url} className="ph" style={{ backgroundImage: `url(${url})` }} />
            ))}
          </div>
        </section>
      )}

      {reviews.length > 0 && (
        <section className="sec reviews" data-testid="section-reviews">
          <div className="sec-h">Reviews</div>
          {reviews.slice(0, 2).map((r, i) => (
            <div className="q" key={i} data-testid={`review-${i}`}>
              <blockquote>&ldquo;{r.quote}&rdquo;</blockquote>
              {r.author?.trim() && <cite>— {r.author}</cite>}
            </div>
          ))}
        </section>
      )}

      {showHours && (
      <section className="sec hours" data-testid="section-hours">
        <div className="sec-h">Hours</div>
        {hasHours ? (
          (hours || []).map(h => (
            <div className={`row ${h.closed ? 'closed' : ''}`} key={h.label}>
              <span className="d">{h.label}</span>
              <span className="h">{h.closed ? 'Closed' : `${formatTime(h.start)} – ${formatTime(h.end)}`}</span>
            </div>
          ))
        ) : (
          <p style={{ opacity: 0.6, fontSize: 14 }}>Hours not set yet.</p>
        )}
      </section>
      )}

      {address && (
        <section className="sec location" data-testid="section-location">
          <div className="sec-h">Location</div>
          <p className="addr">{address}</p>
          <iframe className="map" title="Map" src={mapSrc} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </section>
      )}

      <section className="foot" data-testid="section-footer">
        <button className="book" onClick={() => onBook()} data-testid="footer-book-btn">Book now</button>
        {socialEntries.length > 0 && (
          <div className="social" data-testid="footer-social">
            {socialEntries.map(([k, v]) => (
              <a key={k} href={String(v)} target="_blank" rel="noopener noreferrer">{k}</a>
            ))}
          </div>
        )}
        <div className="fine">© {new Date().getFullYear()} {shop.name}</div>
      </section>
    </>
  );

  // ----- Theme-specific outer scaffolding -----

  // Theme 09 — preface with marquee. Same body as default.
  if (slug === 'kinetic-streetwear') {
    return (
      <div className={`theme-${slug}`} data-testid="shop-home" data-theme={slug}>
        <div className="marq" aria-hidden="true">
          <div className="track">
            <span>Walk-ins welcome ✦</span><span>{shop.name} ✦</span><span>Fresh fades daily ✦</span>
            <span>Walk-ins welcome ✦</span><span>{shop.name} ✦</span><span>Fresh fades daily ✦</span>
          </div>
        </div>
        <div className="shell">
          <div className="top">
            <b><span>//</span>{shop.name}</b>
            {locality && <span>{locality.toUpperCase()}</span>}
          </div>
          <section className="hero">{heroBody}</section>
          {metaLine}
          {Sections}
        </div>
      </div>
    );
  }

  // Theme 10 — aurora gradient bg behind everything.
  if (slug === 'living-aurora') {
    return (
      <div className={`theme-${slug}`} data-testid="shop-home" data-theme={slug}>
        <div className="aurora" aria-hidden="true" />
        <div className="shell">
          <div className="top">
            <b>{shop.name}</b>
            {locality && <span>{locality}</span>}
          </div>
          <section className="hero">{heroBody}</section>
          {metaLine}
          {Sections}
        </div>
      </div>
    );
  }

  // Theme 11 — diagonal clipped dark hero band above body
  if (slug === 'cinematic-split') {
    return (
      <div className={`theme-${slug}`} data-testid="shop-home" data-theme={slug}>
        <div className="top-band">
          <div className="r">
            <b>{shop.name}</b>
            {locality && <span>{locality}</span>}
          </div>
          {heroH1}
          {tagline && <p data-testid="hero-tagline">{tagline}</p>}
        </div>
        <div className="shell">
          {metaLine}
          {Sections}
        </div>
      </div>
    );
  }

  // Theme 04 — curved green topband holds the hero, body in card stack.
  if (slug === 'warm-heritage') {
    return (
      <div className={`theme-${slug}`} data-testid="shop-home" data-theme={slug}>
        <div className="topband">
          <div className="crest">
            <span
              className="mk"
              style={shop.logo_url ? { backgroundImage: `url(${shop.logo_url})`, borderColor: 'transparent' } : {}}
            >
              {!shop.logo_url && initials(shop.name).charAt(0)}
            </span>
            {shop.name}
          </div>
          {heroH1}
          {tagline && <p data-testid="hero-tagline">{tagline}</p>}
        </div>
        <div className="shell">
          <div className="card-stack">
            <div className="card">
              {metaLine}
              {Sections}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Theme 12 — deco frame wrapping everything; centred crest hero
  if (slug === 'gentlemans-club') {
    return (
      <div className={`theme-${slug}`} data-testid="shop-home" data-theme={slug}>
        <div className="shell">
          <div className="deco">
            <div className="deco-in">
              <div className="top"><b>{shop.name}</b></div>
              <div className="hero">
                <div
                  className="logo"
                  style={shop.logo_url ? { backgroundImage: `url(${shop.logo_url})`, color: 'transparent', borderColor: 'transparent' } : {}}
                >
                  {!shop.logo_url && initials(shop.name).charAt(0)}
                </div>
                <div className="est">Est. ·</div>
                {heroH1}
                {tagline && <p data-testid="hero-tagline">{tagline}</p>}
                <div className="rule"><span className="ln"/>✦<span className="ln"/></div>
              </div>
              {metaLine}
              {Sections}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Instacut default (rev-02) — see themes/InstacutDefaultShop.js.
  // Extracted because it owns three page behaviours (topbar pin guard,
  // native <dialog> lightbox, cookie consent + dataLayer events) that
  // need refs and effects. Keeping them here would run those hooks for
  // themes 01–12 too.
  if (slug === 'instacut-default') {
    return (
      <InstacutDefaultShop
        shop={shop}
        services={services}
        barbers={barbers}
        hours={hours}
        showHours={showHours}
        onBook={onBook}
        allowPortal={allowPortal}
      />
    );
  }


  // Default scaffolding for themes 01, 02, 03, 05, 06, 07, 08
  return (
    <div className={`theme-${slug}`} data-testid="shop-home" data-theme={slug}>
      {slug === 'midnight-chrome' && <div className="aurora-glow" aria-hidden="true" />}
      {slug === 'jewel-luxe' && <div className="aurora-glow" aria-hidden="true" />}
      <div className="shell">
        <div className="top">
          <b>{shop.name}</b>
          {locality && <span>{locality}</span>}
        </div>
        <section className="hero">{heroBody}</section>
        {metaLine}
        {Sections}
      </div>
    </div>
  );
};

export default ShopThemed;
